import type { AutoCoderImportConfig } from '../types/gotypes'
import { PipelineLifecycle } from '../types/gotypes'

export type PipelineKind = 'one-shot' | 'long-lived'

export interface CreatePipelineArgs {
  config: AutoCoderImportConfig
  labels?: Record<string, string>
  systemName?: string
  lifecycle: PipelineLifecycle
}

export interface BuildOpts {
  systemName?: string
  labels?: Record<string, string>
  /** The team the pipeline's AI spend is billed to; "" or absent for none. */
  billingTeamId?: string
}

/** The fields of a team the billing-team choice reads. */
export interface BillingTeamOption {
  id: string
  archived_at: number | null
}

/**
 * The billing team a new pipeline is created with: the one chosen when it is
 * still an open team, else the customer's only open team, else none. The
 * server requires a team when the customer has any open team.
 */
export function effectiveBillingTeam(openTeams: BillingTeamOption[], chosen: string): string {
  const open = openTeams.filter((t) => t.archived_at == null)
  if (chosen && open.some((t) => t.id === chosen)) return chosen
  return open.length === 1 ? open[0].id : ''
}

/** Whether a new pipeline needs a billing team: the customer has an open team. */
export function billingTeamRequired(teams: BillingTeamOption[]): boolean {
  return teams.some((t) => t.archived_at == null)
}

// Pipelines no longer own a data source (T20 retired the wizard's Data Source
// step) — data is ingested at job level and sent to a pipeline afterwards. Any
// data_source the caller still carries is dropped from the create payload so a
// new pipeline is never born pointing at a source. The billing team is set
// from the wizard's choice, and left out when there is none.
function createConfig(config: AutoCoderImportConfig, billingTeamId?: string): AutoCoderImportConfig {
  const out = { ...config }
  delete out.data_source
  delete out.billing_team_id
  const team = (billingTeamId ?? '').trim()
  if (team) out.billing_team_id = team
  return out
}

// buildCreatePipelineArgs maps the chosen pipeline kind to the create payload.
// Long-lived pipelines are created active (ready for sources to target by
// system_name); one-shot pipelines keep the legacy auto-completing behaviour.
export function buildCreatePipelineArgs(
  kind: PipelineKind,
  config: AutoCoderImportConfig,
  opts: BuildOpts,
): CreatePipelineArgs {
  if (kind === 'long-lived') {
    return {
      config: createConfig(config, opts.billingTeamId),
      labels: opts.labels,
      systemName: opts.systemName,
      lifecycle: PipelineLifecycle.active,
    }
  }
  return {
    config: createConfig(config, opts.billingTeamId),
    labels: opts.labels,
    lifecycle: PipelineLifecycle.one_shot,
  }
}
